import { onMounted, onBeforeUnmount, ref } from 'vue';

// Polls /version.json so the running SPA can detect a new deploy and prompt
// the user to reload. Build-time VITE_APP_VERSION (set to github.sha by CI)
// is compared against the server's current version.json. Dev builds get
// 'dev' on both sides — polling runs but never triggers.
//
// The same "update available" banner is also reused by the PWA service worker
// (registered in main.ts): when a new SW is waiting, it calls
// `signalServiceWorkerUpdate(reload)` so we surface ONE reload prompt rather
// than a competing parallel mechanism. The poll and the SW are independent
// triggers feeding the same module-level state below.
//
// Why the two triggers have to cooperate on reload: the SW answers *every*
// navigation from its precache (see src/sw.ts), and the browser only re-checks
// sw.js on a navigation — never on a timer. So the poll always spots a deploy
// first, and a bare location.reload() at that point is served the previous
// build the old worker still has cached, which brings the banner straight back.
// Hence: the poll kicks a SW update check the moment it sees a new version, and
// `reload()` hands off to the waiting worker instead of reloading blindly.

const POLL_INTERVAL_MS = 120_000;
// How long "reload now" waits for the service worker to reach `waiting` before
// giving up and reloading directly.
const SW_HANDOFF_TIMEOUT_MS = 4_000;
// Safety net: workbox reloads the page itself once the new worker takes
// control. If that never lands, reload ourselves so the button is never a no-op.
const SW_RELOAD_TIMEOUT_MS = 4_000;
const VERSION_URL = '/version.json';

const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION || 'dev';

type VersionPayload = { version?: string };

// Shared, module-level so both the version poll and the SW registration in
// main.ts (which lives outside Vue's component tree) flip the same flag.
const updateAvailable = ref(false);
// Set while "reload now" is in flight so the button can show progress and
// repeated clicks don't stack another hand-off on top of the pending one.
const reloading = ref(false);
// When the trigger is a waiting SW, reloading must go through the SW's
// skipWaiting flow (set here); otherwise a plain location.reload() suffices.
let serviceWorkerReload: (() => void) | null = null;
// Asks the SW registration to look for a new worker. Null when no SW is
// registered (dev/test builds, unsupported browsers).
let serviceWorkerUpdateCheck: (() => void) | null = null;
// Callbacks parked by `reload()` while it waits for the worker to reach
// `waiting`; resolved by signalServiceWorkerUpdate.
let serviceWorkerWaiters: Array<() => void> = [];
// True once the poll has confirmed the running bundle IS the deployed build.
let runningVersionIsCurrent = false;

/**
 * Called from the SW registration with a way to trigger an update check, so the
 * version poll can start fetching the new worker as soon as it detects a deploy
 * instead of waiting for the browser's own (navigation-only) check.
 */
export function registerServiceWorkerUpdateChecker(check: () => void): void {
  serviceWorkerUpdateCheck = check;
}

/**
 * Called from the SW registration when a new worker is waiting. Surfaces the
 * existing reload banner and routes its "reload now" action through the SW so
 * the fresh assets are activated. No-op-safe: only used in production builds
 * where the SW is registered.
 */
export function signalServiceWorkerUpdate(activateAndReload: () => void): void {
  serviceWorkerReload = activateAndReload;
  const waiters = serviceWorkerWaiters;
  serviceWorkerWaiters = [];
  waiters.forEach((resolve) => resolve());
  // A waiting worker while we already run the deployed build only means the
  // precache is one build behind — it activates on the next navigation, so
  // there is nothing worth interrupting the user for.
  if (!runningVersionIsCurrent) updateAvailable.value = true;
}

/** Resolves true once a waiting worker is available, false on timeout. */
function waitForServiceWorker(timeoutMs: number): Promise<boolean> {
  if (serviceWorkerReload) return Promise.resolve(true);
  return new Promise((resolve) => {
    const onReady = () => resolve(true);
    serviceWorkerWaiters.push(onReady);
    setTimeout(() => {
      serviceWorkerWaiters = serviceWorkerWaiters.filter((waiter) => waiter !== onReady);
      // No-op once onReady already settled the promise.
      resolve(false);
    }, timeoutMs);
  });
}

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as VersionPayload;
    return typeof body.version === 'string' ? body.version : null;
  } catch {
    return null;
  }
}

export function useVersionCheck() {
  let intervalId: ReturnType<typeof setInterval> | undefined;

  const check = async () => {
    const remote = await fetchRemoteVersion();
    // Unreachable version.json (offline, transient 5xx): keep whatever the
    // banner already shows rather than guessing.
    if (!remote) return;

    if (remote === CURRENT_VERSION) {
      runningVersionIsCurrent = true;
      // Clears a prompt a waiting worker raised before the poll could confirm
      // we are in fact already on the deployed build.
      updateAvailable.value = false;
      return;
    }

    runningVersionIsCurrent = false;
    updateAvailable.value = true;
    // Start the worker update now so "reload now" can hand off immediately
    // instead of falling back to a reload the old worker would answer stale.
    if (!serviceWorkerReload) serviceWorkerUpdateCheck?.();
  };

  const onVisibility = () => {
    if (!document.hidden) void check();
  };

  onMounted(() => {
    // Skip polling in dev where version.json isn't served.
    if (CURRENT_VERSION === 'dev') return;
    void check();
    intervalId = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
  });

  onBeforeUnmount(() => {
    if (intervalId) clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibility);
  });

  return {
    updateAvailable,
    reloading,
    currentVersion: CURRENT_VERSION,
    reload: async (): Promise<void> => {
      if (reloading.value) return;
      reloading.value = true;

      // No service worker in play (dev/test, unsupported browser): nothing to
      // hand off to, and nothing can serve us a stale shell either.
      if (!serviceWorkerReload && !serviceWorkerUpdateCheck) {
        window.location.reload();
        return;
      }

      serviceWorkerUpdateCheck?.();
      const handedOff = await waitForServiceWorker(SW_HANDOFF_TIMEOUT_MS);
      if (handedOff && serviceWorkerReload) {
        serviceWorkerReload();
        setTimeout(() => window.location.reload(), SW_RELOAD_TIMEOUT_MS);
        return;
      }

      // Worker never showed up (update fetch failed/slow). The shell is
      // network-first in src/sw.ts, so a direct reload still lands on the new
      // build as long as we're online.
      window.location.reload();
    },
  };
}
